import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Actualites from "@/pages/Actualites";
import Members from "@/pages/Members";
import Users from "@/pages/Users";
import Staff from "@/pages/Staff";
import Coaches from "@/pages/Coaches";
import Emails from "@/pages/Emails";
import Committee from "@/pages/Committee";
import CommitteePublic from "@/pages/CommitteePublic";
import Categories from "@/pages/Categories";
import Planning from "@/pages/Planning";
import Attendance from "@/pages/Attendance";
import Trainings from "@/pages/Trainings";
import Skills from "@/pages/Skills";
import Cotisations from "@/pages/Cotisations";
import ClubInfo from "@/pages/ClubInfo";
import Sponsors from "@/pages/Sponsors";
import Locations from "@/pages/Locations";
import Droits from "@/pages/Droits";
import Audit from "@/pages/Audit";
import Comptabilite from "@/pages/Comptabilite";
import Equipment from "@/pages/Equipment";
import Volunteers from "@/pages/Volunteers";
import Infographics from "@/pages/Infographics";
import PaymentResult from "@/pages/PaymentResult";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

function Protected({ children, roles, committee, treasurer, volunteer }) {
  const { user, loading, hasRole, committee: isCommittee, treasurer: isTreasurer, volunteer: isVolunteer } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !hasRole(...roles)) return <Navigate to="/" replace />;
  if (committee && !(hasRole("admin") || isCommittee)) return <Navigate to="/" replace />;
  if (treasurer && !(hasRole("admin") || isTreasurer)) return <Navigate to="/" replace />;
  if (volunteer && !(hasRole("admin") || isVolunteer)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={loading ? <Loader /> : user ? <Navigate to="/" replace /> : <Auth />} />
      <Route path="/payment/success" element={<PaymentResult status="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult status="cancel" />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/actualites" element={<Protected><Actualites /></Protected>} />
      <Route path="/membres" element={<Protected><Members /></Protected>} />
      <Route path="/utilisateurs" element={<Protected roles={["admin"]}><Users /></Protected>} />
      <Route path="/emails" element={<Protected roles={["admin"]}><Emails /></Protected>} />
      <Route path="/annuaire" element={<Protected><Staff /></Protected>} />
      <Route path="/coachs" element={<Protected><Coaches /></Protected>} />
      <Route path="/comite" element={<Protected committee><Committee /></Protected>} />
      <Route path="/benevoles" element={<Protected volunteer><Volunteers /></Protected>} />
      <Route path="/notre-comite" element={<Protected><CommitteePublic /></Protected>} />
      <Route path="/categories" element={<Protected roles={["admin", "coach"]}><Categories /></Protected>} />
      <Route path="/planning" element={<Protected><Planning /></Protected>} />
      <Route path="/presence" element={<Protected roles={["admin", "coach", "parent", "player"]}><Attendance /></Protected>} />
      <Route path="/entrainements" element={<Protected roles={["admin", "coach"]}><Trainings /></Protected>} />
      <Route path="/competences" element={<Protected><Skills /></Protected>} />
      <Route path="/cotisations" element={<Protected roles={["admin", "player", "parent"]}><Cotisations /></Protected>} />
      <Route path="/pack" element={<Protected><Equipment /></Protected>} />
      <Route path="/droits" element={<Protected roles={["admin"]}><Droits /></Protected>} />
      <Route path="/audit" element={<Protected roles={["admin"]}><Audit /></Protected>} />
      <Route path="/infographies" element={<Protected roles={["admin"]}><Infographics /></Protected>} />
      <Route path="/comptabilite" element={<Protected treasurer><Comptabilite /></Protected>} />
      <Route path="/club" element={<Protected><ClubInfo /></Protected>} />
      <Route path="/sponsors" element={<Protected><Sponsors /></Protected>} />
      <Route path="/lieux" element={<Protected roles={["admin", "coach"]}><Locations /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
